import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, Building2, MapPin, Mail, Phone } from "lucide-react";
import { api } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUS = ["new", "contacted", "approved", "rejected"];
const STATUS_COLOR = {
  new: "text-sky-400 border-sky-400/40 bg-sky-400/10",
  contacted: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  approved: "text-lime2 border-lime2/40 bg-lime2/10",
  rejected: "text-red-400 border-red-400/40 bg-red-400/10",
};

export const AdminDistributors = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/distributors");
      setApps(data);
    } catch {
      toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await api.patch(`/admin/distributors/${id}`, { status });
    } catch { toast.error("Update failed"); load(); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/distributors/${id}`);
      toast.success("Application removed");
      setApps((prev) => prev.filter((a) => a.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const counts = STATUS.reduce((acc, s) => ({ ...acc, [s]: apps.filter((a) => a.status === s).length }), {});

  return (
    <div data-testid="distributors-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-white/50">{apps.length} application{apps.length === 1 ? "" : "s"}</span>
          {STATUS.map((s) => (
            <span key={s} className={`text-xs rounded-full border px-3 py-1 capitalize ${STATUS_COLOR[s]}`} data-testid={`dist-count-${s}`}>
              {s}: {counts[s] || 0}
            </span>
          ))}
        </div>
        <button onClick={load} data-testid="dist-refresh" className="grid place-items-center w-10 h-10 rounded-lg border border-white/10 hover:border-lime2 hover:text-lime2 transition-colors">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#181818] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="distributors-table">
            <thead>
              <tr className="text-left text-white/40 text-xs uppercase tracking-[0.15em] border-b border-white/10">
                <th className="px-5 py-4 font-medium">Company</th>
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">Country</th>
                <th className="px-5 py-4 font-medium">Message</th>
                <th className="px-5 py-4 font-medium">Status</th>
                <th className="px-5 py-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-white/40">Loading...</td></tr>
              ) : apps.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-white/40" data-testid="no-distributors">No applications yet.</td></tr>
              ) : apps.map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`dist-row-${a.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-medium inline-flex items-center gap-1.5"><Building2 size={13} className="text-emerald2" /> {a.company}</p>
                    <p className="text-white/40 text-xs mt-0.5">{a.name} · {new Date(a.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-5 py-4 text-white/70">
                    <p className="inline-flex items-center gap-1.5"><Mail size={12} /> {a.email}</p>
                    <p className="text-white/40 text-xs mt-0.5 inline-flex items-center gap-1.5"><Phone size={12} /> {a.phone}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 text-xs text-lime2"><MapPin size={12} /> {a.country}</span>
                    {a.city && <p className="text-white/40 text-xs mt-0.5 pl-4">{a.city}</p>}
                  </td>
                  <td className="px-5 py-4 text-white/60 max-w-xs">
                    <p className="line-clamp-2">{a.message || "—"}</p>
                  </td>
                  <td className="px-5 py-4">
                    <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                      <SelectTrigger data-testid={`dist-status-${a.id}`} className={`h-8 w-32 text-xs border capitalize ${STATUS_COLOR[a.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => remove(a.id)} data-testid={`dist-delete-${a.id}`} className="text-white/30 hover:text-red-400 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
